import { Elements } from "@stripe/react-stripe-js"
import { NAME_OF_PRODUCT_TYPES, stripePromise } from "helpers/sf_constants"
import LoadingSpinner from "components/Common/LoadingSpinner"
import React, { useMemo } from "react"
import { lazy } from "react"
import { Suspense } from "react"
import UnlockAdminUserAccount from "pages/Admin/Authentication/UnlockAdminUserAccount";
import ScheduledReportLog from "pages/Analytics/ScheduledReportLog/ScheduledReportLog"
import EditIntegration from "pages/integrations/edit-integration/EditIntegration"
import Leads from "pages/Leads/Leads"
import LeadDetail from "pages/Leads/LeadDetail"
import Calls from "pages/Calls/Calls"
import CallTranscription from "pages/CallsTranscription/CallTranscription"
import { getAppropriateLoadingGif } from "helpers/sf_utils"
import useFeatureFlags from 'hooks/useFeatureFlags'
import { REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants'
import { AppConfiguration, isGoalBrand } from "config"
import WhitelistedBrandsUsers from "pages/Admin/WhitelistedBrands/WhitelistedBrandUsers"
import QuoteFunnelAnswerLayout from "pages/QuoteFunnelAnswer/QuoteFunnelAnswerLayout"
import ReviewQuoteFunnelAnswer from "pages/QuoteFunnelAnswer/ReviewQuoteFunnelAnswer"
import QuoteFunnelPreview from "pages/QuoteFunnel/QuoteFunnelPreview"
import TermsOfUseQF from "pages/TermsOfServices/TermsOfUseQF"
import PrivacyPolicyQF from "pages/TermsOfServices/PrivacyPolicyQF"
import TermsSMSQF from "pages/TermsOfServices/TermsSMSQF"
import CCPAPolicyQf from "pages/TermsOfServices/CCPAPolicyQf"
import ProspectDetail from "pages/Leads/ProspectDetail"
import Conversations from "pages/Leads/call-summary/Conversations"
import DevDashboard from "pages/Admin/DevDashboard"
import ReportingConsistencyRoute from "pages/Admin/ReportingConsistency/ReportingConsistencyRoute"
import DmsDashboard from "pages/Admin/DmsDashboard"
import RulesDashboard from "pages/Admin/RulesDashboard"
import ReceiptTypes from "pages/Accounting/ReceiptTypes/ReceiptTypes"
import StatusMonitor from "pages/Admin/StatusMonitor"
import AllCustomerIncidents from "pages/Admin/StatusMonitor/AllCustomerIncidents"
import AutomationTests from "pages/Admin/AutomationTests"
import DataScience from "pages/Admin/DataScience/DataScience"
import RdsMonitor from "pages/Admin/RdsMonitor"
import { getAdminDashboardComponent } from "helpers/sf_feature_flag_helper"
import AdminLeadIntegrationsLogs from "pages/Admin/AdminLeadIntegrationsLogs"
import AdminBulkMigration from "pages/Admin/AdminBulkEditJobs"
import RefundPolicy from "pages/RefundPolicies/RefundPolicies"
import AdminCampaignFulFillment from "pages/Admin/AdminCampaignFulFillment"
import MiloDashboard from "pages/Admin/Milo"
import QfCampaignsPage from "pages/Campaign/CampaignList/QfCampaigns"
import CampaignFulfillment from "pages/Campaign/CampaignFulfillment"

const lazyWithRetry = componentImport =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem("page-has-been-force-refreshed") || "false"
    )

    try {
      const component = await componentImport()

      window.localStorage.setItem("page-has-been-force-refreshed", "false")

      return component
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        window.localStorage.setItem("page-has-been-force-refreshed", "true")
        //ToastAlert.fire({ icon: 'info', title: 'There is an app update for this page... Fetching new version' });
        return window.location.reload()
      }

      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      // Let's let the application crash and raise the error.
      throw error
    }
  })
const Dashboard = lazyWithRetry(() => import("../../pages/Dashboard/index"))

const CampaignsPage = lazyWithRetry(() =>
  import("../../pages/Campaign/CampaignList/Campaigns")
)
const Settings = lazyWithRetry(() => import("../../pages/Settings/Settings"))
const CampaignSummary = lazyWithRetry(() =>
  import("../../pages/Campaign/CampaignSummary/CampaignSummary")
)
const Analytics = lazyWithRetry(() => import("../../pages/Analytics/Analytics"))
const ScheduledReportList = lazyWithRetry(() =>
  import("../../pages/Analytics/ScheduledReportList/ScheduledReportList")
)
const AdminScheduledReportList = lazyWithRetry(() =>
  import("../../pages/Admin/DashboardRevamp/AdminScheduledReportList/AdminScheduledReportList")
)
const AdminScheduledReportLog = lazyWithRetry(() =>
  import("../../pages/Admin/DashboardRevamp/AdminScheduledReportLog/AdminScheduledReportLog")
)

const Register = lazyWithRetry(() =>
  import("../../pages/Authentication/Register")
)
const ResetPassword = lazyWithRetry(() =>
  import("../../pages/Authentication/ResetPassword")
)
const Users = lazyWithRetry(() => import("../../pages/Roles/Users"))
const ForgetPwd = lazyWithRetry(() =>
  import("../../pages/Authentication/ForgetPassword")
)
const CreateRole = lazyWithRetry(() => import("../../pages/Roles/CreateRole"))
const Roles = lazyWithRetry(() => import("../../pages/Roles/roles"))
const Forbidden = lazyWithRetry(() =>
  import("../../components/Common/Forbidden")
)

const CreateNewCompany = lazyWithRetry(() =>
  import("../../pages/Company/CreateNewCompany")
)
const AddNewCreative = lazyWithRetry(() =>
  import(
    "../../pages/Creative/CreativeComponents/AddNewCreative/AddNewCreative"
  )
)
const AllCreatives = lazyWithRetry(() =>
  import("../../pages/Creative/CreativeComponents/CreativeList/AllCreatives")
)

const CreateCampaign = lazyWithRetry(() =>
  import("../../pages/Campaign/CreateCampaign/CreateCampaign")
)
const EditCampaign = lazyWithRetry(() =>
  import("../../pages/Campaign/EditCampaign/EditCampaign")
)
const ChangeLogs = lazyWithRetry(() =>
  import("../../pages/ChangeLog/ChangeLogs")
)

const AdminChangeLogs = lazyWithRetry(() =>
  import("../../pages/ChangeLog/AdminChangeLogs")
)

const Login = lazyWithRetry(() => import("../../pages/Authentication/Login"))
const Logout = lazyWithRetry(() => import("../../pages/Authentication/Logout"))
const UserProfile = lazyWithRetry(() =>
  import("../../pages/Authentication/user-profile")
)
const AdGroups = lazyWithRetry(() =>
  import("../../pages/Campaign/AdGroups/AdGroups")
)

const AdGroupsV4 = lazyWithRetry(() =>
  import("../../pages/Campaign/AdGroups/AdGroupsV4")
)

const Profiles = lazyWithRetry(() =>
  import("../../pages/Campaign/Profiles/Profiles")
)

const Billing = lazyWithRetry(() => import("../../pages/Billing/Billing"))
const BillingInformation = lazyWithRetry(() =>
  import("../../pages/Authentication/BillingInformation")
)

const FeatureFlags = lazyWithRetry(() =>
  import("../../pages/Admin/Feature-Flags/FeatureFlagsComponents/FeatureFlags")
)
const SourceSetting = lazyWithRetry(() =>
  import("../../pages/Campaign/SourceSetting/SourceSetting")
)

const AdminUsers = lazyWithRetry(() =>
  import("../../pages/Admin/Users/UserComponents/UserList/AdminUsers")
)

const Notifications = lazyWithRetry(() =>
  import("../../pages/Notifications/Notifications")
)

const Integrations = lazyWithRetry(() =>
  import("../../pages/integrations/Integrations")
)

const CreateIntegration = lazyWithRetry(() =>
  import("../../pages/integrations/create-integration/CreateIntegration")
)

const ClickConversionLogs = lazyWithRetry(() =>
  import("../../pages/Settings/SettingComponents/ClickConversionLogs")
)

const ConversionUploadDetails = lazyWithRetry(() =>
  import("../../pages/Settings/SettingComponents/ConversionUploadDetails")
)

// ---------------- ADMIN USERS AUTH START --------------
const AdminLogin = lazyWithRetry(() =>
  import("../../pages/Admin/Authentication/AdminUserLogin")
)
const ResetAdminUserPassword = lazyWithRetry(() =>
  import("../../pages/Admin/Authentication/ResetAdminUserPassword")
)
const AdminSignup = lazyWithRetry(() =>
  import("../../pages/Admin/Authentication/AdminUserSignUp")
)
const AdminUserProfile = lazyWithRetry(() =>
  import("../../pages/Admin/Authentication/AdminUserProfile")
)

const WhitelistedBrands = lazyWithRetry(() =>
  import("../../pages/Admin/WhitelistedBrands/WhitelistedBrands")
)
const AdminUserRoles = lazyWithRetry(() =>
  import("../../pages/Admin/Roles/AdminUserRoles")
)
const AdminNotificationTemplates = lazyWithRetry(() =>
  import("../../pages/Admin/Notifications/AdminNotificationTemplates")
)
const AdminNotifications = lazyWithRetry(() =>
  import("../../pages/Admin/Notifications/AdminNotifications")
)
const EditAdminNotificationTemplates = lazyWithRetry(() =>
  import("../../pages/Admin/Notifications/EditAdminNotificationTemplate")
)
const AdminInternalUser = lazyWithRetry(() =>
  import("../../pages/Admin/AdminInternalUser/AdminInternalUser")
)
const EditAdminUserRole = lazyWithRetry(() =>
  import("../../pages/Admin/Roles/EditAdminUserRole")
)
const AdminUserForgotPassword = lazyWithRetry(() =>
  import("../../pages/Admin/Authentication/ForgetAdminUserPassword")
)
const EmailEvents = lazyWithRetry(() =>
  import("../../pages/EmailEvents/EmailEvents")
)
const ReportExports = lazyWithRetry(() =>
  import("../../pages/Admin/ReportExports/ReportExports")
)
const Invoices = lazyWithRetry(() =>
  import("../../pages/Accounting/Invoices/Invoices.js")
)
const Receipts = lazyWithRetry(() =>
  import("../../pages/Accounting/Receipts/Receipts.js")
)
const CampaignAnalysisPage = lazyWithRetry(() =>
  import("../../pages/CampaignAnalysis/CampaignAnalysisPage.js")
)

const CampaignAnalysisLeadsCalls = lazyWithRetry(() =>
  import("../../pages/CampaignAnalysis/CampaignAnalysisLeadsCalls.js")
)

const SourceSettingForLeadAndCall = lazyWithRetry(() =>
  import("../Campaign/SourceSettingsForLeadsAndCalls/LeadsCallsSourceSetting.js")
)

const IntegrationLog = lazyWithRetry(() =>
  import("../../pages/integrations/integration-log/IntegrationLog.js")
)

const ClientEmailEvents = lazyWithRetry(() =>
  import("../../pages/EmailEvents/ClientEmailEvents.js")
)

const InsuranceCarriers = lazyWithRetry(() =>
  import("../AdminInsuranceCarriers/AdminInsuranceCarriers.js")
)

const UserActivity = lazyWithRetry(() =>
  import("../UserActivity/UserActivity.js")
)

const AdminRefundReasons = lazyWithRetry(() =>
  import("../AdminRefundReasons/AdminRefundReasons.js")
)

const TermsOfServices = lazyWithRetry(() =>
  import("../TermsOfServices/TermsOfServices.js")
)

const ClientTermsOfServices = lazyWithRetry(() =>
  import("../TermsOfServices/ClientTermsOfServices.js")
)

const AdminPaymentTerms = lazyWithRetry(() =>
  import("../AdminPaymentTerms/AdminPaymentTerms.js")
)

const AdminBillingEvents = lazyWithRetry(() =>
  import("../AdminBillingEvents/AdminBillingEvents.js")
)

const AdminTwilioNumbers = lazyWithRetry(() =>
  import("../AdminTwilioNumbers/AdminTwilioNumbers.js")
)

const AdminLeadPrices = lazyWithRetry(() =>
  import("../AdminLeads/LeadPrices/AdminLeadPrices.js")
)

const AdminCallPrices = lazyWithRetry(() =>
  import("../AdminCalls/CallPrices/AdminCallPrices.js")
)

const AdminLeadRefunds = lazyWithRetry(() =>
  import("../AdminLeads/LeadRefund/AdminLeadRefunds.js")
)

const AdminSfFilters = lazyWithRetry(() =>
  import("../AdminSfFilters/AdminSfFilters.js")
)

const AdminFilterPackages = lazyWithRetry(() =>
  import("../AdminFilterPackages/AdminFilterPackages.js")
)

const QuoteFunnel = lazyWithRetry(() =>
  import("../QuoteFunnel/index.js")
)

const Prospects = lazyWithRetry(() =>
  import("../Prospects/index.js")
)

const CPAOptimizer = lazyWithRetry(() =>
  import("../CPAOptimizer/index.js")
)

// ---------------- ADMIN USERS AUTH END --------------

export const DashboardLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Dashboard />
  </Suspense>
)

export const CampaignsPageLazy = () => {
  const [isFeatureEnabled] = useFeatureFlags();
  const isQuoteFunnelAggregationEnabled = useMemo(
    () => isFeatureEnabled(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_QF_CAMPAIGNS_AGGREGATED) && isGoalBrand,
    [isFeatureEnabled]
  );

  return (
    <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
      {isQuoteFunnelAggregationEnabled ? <QfCampaignsPage /> : <CampaignsPage />}
    </Suspense>
  );
}

export const LeadsPageLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Leads />
  </Suspense>
)

export const LeadDetailPageLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <LeadDetail />
  </Suspense>
)

export const ProspectDetailPageLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ProspectDetail />
  </Suspense>
)
export const CallsPageLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Calls />
  </Suspense>
)

const ParsedCallDetails = lazyWithRetry(() =>
  import("../../pages/Calls/ParsedCalls/ParsedCallDetails")
)

export const ParsedCallDetailsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ParsedCallDetails />
  </Suspense>
)

export const CallTranscriptionLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CallTranscription />
  </Suspense>
)


export const CampaignSummaryLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CampaignSummary />
  </Suspense>
)
export const UserProfileLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <UserProfile />
  </Suspense>
)

export const LogoutLazy = () => (
  <Suspense fallback={<div>Page is Loading...</div>}>
    <Logout />
  </Suspense>
)

export const CreateNewCompanyLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CreateNewCompany />
  </Suspense>
)

export const RolesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Roles />
  </Suspense>
)

export const CreateRoleLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CreateRole />
  </Suspense>
)

export const ForbiddenLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Forbidden />
  </Suspense>
)

export const UsersLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Users />
  </Suspense>
)

export const CreateCampaignLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CreateCampaign />
  </Suspense>
)

export const EditCampaignLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <EditCampaign />
  </Suspense>
)

export const AdGroupsLazy = () => {
  const [isFeatureEnabled] = useFeatureFlags();
  const isAdGroupStatsV4Enabled = useMemo(
    () => isFeatureEnabled(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_AD_GROUP_STATS_V4),
    [isFeatureEnabled]
  );

  return (
    <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
      {isAdGroupStatsV4Enabled ? <AdGroupsV4 /> : <AdGroups />}
    </Suspense>
  );
}

export const ProfilesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Profiles />
  </Suspense>
)
export const SourceSettingLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <SourceSetting />
  </Suspense>
)

export const SettingsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Settings />
  </Suspense>
)

export const AllCreativesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AllCreatives />
  </Suspense>
)

export const AddNewCreativeLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AddNewCreative />
  </Suspense>
)

export const ChangeLogsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ChangeLogs />
  </Suspense>
)

export const AdminChangeLogsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminChangeLogs />
  </Suspense>
)

export const LoginLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Login />
  </Suspense>
)

export const ForgetPwdLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ForgetPwd />
  </Suspense>
)

export const RegisterLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Register />
  </Suspense>
)

export const ResetPasswordLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ResetPassword />
  </Suspense>
)

export const CreatePasswordLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ResetPassword create={true} />
  </Suspense>
)

export const AnalyticsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Analytics />
  </Suspense>
)

export const ScheduledReportLogLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ScheduledReportLog />
  </Suspense>
)

export const BillingLazy = () => (
  <Suspense fallback={<div>Page is Loading...</div>}>
    <Elements stripe={stripePromise}>
      <Billing />
    </Elements>
  </Suspense>
)

export const BillingInformationLazy = () => (
  <Suspense fallback={<div>Page is Loading...</div>}>
    <Elements stripe={stripePromise}>
      <BillingInformation />
    </Elements>
  </Suspense>
)

export const AdminUsersLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminUsers />
  </Suspense>
)

export const ScheduledReportListLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ScheduledReportList />
  </Suspense>
)

export const AdminScheduledReportListLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminScheduledReportList />
  </Suspense>
)

export const AdminScheduledReportLogLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminScheduledReportLog />
  </Suspense>
)

export const FeaturesFlagsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <FeatureFlags />
  </Suspense>
)

export const quoteFunnelAnswersFlagsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <QuoteFunnelAnswerLayout />
  </Suspense>
)

export const termsOfUseLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <TermsOfUseQF />
  </Suspense>
)

export const privacyPolicyQFLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <PrivacyPolicyQF />
  </Suspense>
)

export const SMSTermsQFLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <TermsSMSQF />
  </Suspense>
)

export const CCPAPolicyQFLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CCPAPolicyQf />
  </Suspense>
)

export const quoteFunnelReviewFlagsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ReviewQuoteFunnelAnswer />
  </Suspense>
)

// ---------------- ADMIN USERS AUTH START--------------
export const AdminLoginLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminLogin />
  </Suspense>
)

export const AdminSignupLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminSignup />
  </Suspense>
)

export const AdminUserProfileLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminUserProfile />
  </Suspense>
)

export const AdminDashboardLazy = ({ isRevampEnabled }) => {
  const AdminDashboard = useMemo(() => lazyWithRetry(getAdminDashboardComponent(isRevampEnabled)), [isRevampEnabled]);
  return (
    <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
      <AdminDashboard />
    </Suspense>
  );
};

export const WhitelistedBrandsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <WhitelistedBrands />
  </Suspense>
)

export const WhitelistedBrandsUsersLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <WhitelistedBrandsUsers />
  </Suspense>
)

export const AdminUserRolesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminUserRoles />
  </Suspense>
)

export const AdminNotificationTemplatesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminNotificationTemplates />
  </Suspense>
)

export const AdminNotificationsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminNotifications />
  </Suspense>
)

export const EditAdminNotificationTemplatesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <EditAdminNotificationTemplates />
  </Suspense>
)

export const AdminInternalUserLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminInternalUser />
  </Suspense>
)

export const ResetAdminUserPasswordLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ResetAdminUserPassword />
  </Suspense>
)
export const AdminUserForgotPasswordLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminUserForgotPassword />
  </Suspense>
)
export const EditAdminUserRoleLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <EditAdminUserRole />
  </Suspense>
)

export const NotificationsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Notifications />
  </Suspense>)
export const UnlockAdminUserAccountLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <UnlockAdminUserAccount />
  </Suspense>
)

export const EmailEventsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <EmailEvents />
  </Suspense>
)

export const ReportExportsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ReportExports />
  </Suspense>
)

export const InvoicesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Invoices />
  </Suspense>
)

export const ReceiptsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Receipts />
  </Suspense>
)
export const CampaignAnalysisLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CampaignAnalysisPage />
  </Suspense>
)

export const CampaignAnalysisLeadsCallsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CampaignAnalysisLeadsCalls />
  </Suspense>
)

export const IntegrationsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <Integrations />
  </Suspense>
)


export const CreateIntegrationLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CreateIntegration />
  </Suspense>
)

export const EditIntegrationLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <EditIntegration />
  </Suspense>
)

export const CallLeadSourceSettingLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <SourceSettingForLeadAndCall />
  </Suspense>
)

export const ClickConversionLogsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ClickConversionLogs />
  </Suspense>
)

export const ConversionUploadDetailsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ConversionUploadDetails />
  </Suspense>
)

export const CampaignFulfillmentLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <CampaignFulfillment />
  </Suspense>
)

export const IntegrationLogLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <IntegrationLog />
  </Suspense>
)

export const ClientEmailEventsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ClientEmailEvents />
  </Suspense>
)

export const InsuranceCarriersLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <InsuranceCarriers />
  </Suspense>
)

export const UserActivityLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <UserActivity />
  </Suspense>
)

export const AdminRefundReasonsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminRefundReasons />
  </Suspense>
)

export const AdminBillingEventsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminBillingEvents />
  </Suspense>
)

export const AdminPaymentTermsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminPaymentTerms />
  </Suspense>
)

export const TermsOfServicesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <TermsOfServices />
  </Suspense>
)

export const ClientTermsOfServicesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ClientTermsOfServices />
  </Suspense>
)

export const AdminTwilioNumbersLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminTwilioNumbers />
  </Suspense>
)

export const AdminLeadPricesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminLeadPrices />
  </Suspense>
)

export const AdminCallPricesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminCallPrices />
  </Suspense>
)

export const AdminLeadRefundsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminLeadRefunds />
  </Suspense>
)

export const AdminSfClicksFiltersLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <AdminSfFilters product_type={NAME_OF_PRODUCT_TYPES.CLICKS} />
  </Suspense>
)
export const AdminSfLeadsFiltersLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <AdminSfFilters product_type={NAME_OF_PRODUCT_TYPES.LEADS} />
  </Suspense>
)

export const AdminSfCallsFiltersLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <AdminSfFilters product_type={NAME_OF_PRODUCT_TYPES.CALLS} />
  </Suspense>
)

export const AdminLeadFilterPackagesLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <AdminFilterPackages product_type={NAME_OF_PRODUCT_TYPES.LEADS} />
  </Suspense>
)

export const AdminCallFilterPackagesLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <AdminFilterPackages product_type={NAME_OF_PRODUCT_TYPES.CALLS} />
  </Suspense>
)

export const QuoteFunnelLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <QuoteFunnel />
  </Suspense>
)

export const QuoteFunnelPreviewLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <QuoteFunnelPreview />
  </Suspense>
)

export const ProspectPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <Prospects />
  </Suspense>
)

export const CPAOptimizerLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <CPAOptimizer />
  </Suspense>
)

export const ConversationsPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <Conversations />
  </Suspense>
)
// ---------------- ADMIN USERS AUTH END--------------

export const DevDashboardPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <DevDashboard />
  </Suspense>
)

export const ReportingConsistencyPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <ReportingConsistencyRoute />
  </Suspense>
)

export const DmsDashobardPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <DmsDashboard />
  </Suspense>
)

export const StatusMonitorPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <StatusMonitor />
  </Suspense>
)

export const AutomationTestsPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <AutomationTests />
  </Suspense>
)

export const DataSciencePageLazy = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <DataScience />
    </Suspense>
  )
}

export const CustomerIncidentsPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <AllCustomerIncidents />
  </Suspense>
)

export const RulesDashobardPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <RulesDashboard />
  </Suspense>
)

export const ReceiptTypesTermsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <ReceiptTypes />
  </Suspense>
)

export const RdsMonitorPageLazy = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <RdsMonitor />
  </Suspense>
)

export const AdminIntegrationLogsLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminLeadIntegrationsLogs />
  </Suspense>
)

export const AdminBulkMigrationLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminBulkMigration />
  </Suspense>
)

export const RefundPoliciesLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <RefundPolicy />
  </Suspense>
)

export const AdminCampaignFulFillmentLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <AdminCampaignFulFillment />
  </Suspense>
)

export const MiloDashboardLazy = () => (
  <Suspense fallback={getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)}>
    <MiloDashboard />
  </Suspense>
)
